'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminLoginPage() {
  const router = useRouter();
  const { signIn, session, loading, userRole, signOut } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // If already authenticated as admin, redirect immediately
  useEffect(() => {
    if (!loading && session && userRole === 'admin') {
      router.replace('/admin-dashboard');
    }
  }, [loading, session, userRole, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setSubmitting(true);
    setAuthError(null);

    try {
      const result = await signIn(email, password);
      if (result?.session) {
        const role =
          result.user?.user_metadata?.role ||
          result.user?.app_metadata?.role ||
          'partner';
        if (role === 'admin') {
          router.push('/admin-dashboard');
        } else {
          // Sign out immediately — non-admin must not remain logged in via admin portal
          await signOut();
          setAuthError("Accès refusé. Ce portail est réservé aux administrateurs.");
        }
      } else {
        setAuthError('Identifiants incorrects. Veuillez vérifier votre e-mail et votre mot de passe.');
      }
    } catch (error: any) {
      setAuthError('Identifiants incorrects. Veuillez vérifier votre e-mail et votre mot de passe.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <AppLogo size={40} />
          <div>
            <span className="text-lg font-bold text-foreground">Mon Plan Rénov</span>
            <p className="text-xs text-muted-foreground">Administration</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-1">Connexion Admin</h1>
            <p className="text-sm text-muted-foreground">Accès réservé aux administrateurs.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Auth error */}
            {authError && (
              <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-lg px-4 py-3">
                <Icon name="ExclamationCircleIcon" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-primary">{authError}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="admin-email" className="block text-sm font-medium text-foreground mb-1.5">
                Adresse e-mail
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                placeholder="admin@email.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                suppressHydrationWarning
                className="w-full bg-input border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-foreground mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  suppressHydrationWarning
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  suppressHydrationWarning
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !email || !password}
              suppressHydrationWarning
              className="w-full bg-primary hover:bg-accent text-primary-foreground font-semibold py-3 px-6 rounded-lg transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm mt-2"
            >
              {submitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  <span>Connexion en cours…</span>
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          © 2026 Mon Plan Rénov · Tous droits réservés
        </p>
      </div>
    </div>
  );
}
